import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Card } from "./ui/card";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  MessageSquare,
  CheckCircle,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactMethods = [
  {
    icon: Phone,
    title: "Phone",
    description: "Call us directly",
    value: "(407) 949-4656",
    action: "tel:+14079494656",
  },
  {
    icon: Mail,
    title: "Email",
    description: "Send us an email",
    value: "sales@rollorlando.com",
    action: "mailto:sales@rollorlando.com",
  },
  {
    icon: MapPin,
    title: "Service Area",
    description: "Serving Central Florida",
    value: "Orlando & Surrounding Areas\nOrange, Seminole, Osceola Counties",
    action: null,
  },
  {
    icon: Clock,
    title: "Hours",
    description: "Business hours",
    value: "Mon-Fri: 7AM - 6PM\nSat: 8AM - 2PM",
    action: null,
  },
];

export const Contact = ({ showForm = true }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.subject.trim() ||
      !formData.message.trim()
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      // Submit to Web3Forms
      const web3FormsData = {
        access_key: "f1efe876-6607-4def-ab09-05f4daff61fb",
        name: formData.name,
        email: formData.email,
        phone: formData.phone || "Not provided",
        subject: formData.subject,
        message: formData.message,
        from_name: "Roll Orlando Website",
      };

      const web3Response = await axios.post("https://api.web3forms.com/submit", web3FormsData);
      
      if (web3Response.data.success) {
        // Also save to our backend for record keeping
        try {
          await axios.post(`${API}/contact`, formData);
        } catch (backendError) {
          // Don't fail if backend save fails, web3forms already sent the email
          console.log("Backend save failed, but email was sent");
        }
        
        setIsSubmitted(true);
        toast.success("Message sent successfully!");
      } else {
        throw new Error("Web3Forms submission failed");
      }
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      data-testid="contact-section"
      className="py-20 sm:py-24 bg-white relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="text-center mb-12 sm:mb-14">
          <span className="inline-block text-xs sm:text-sm font-medium uppercase tracking-widest text-brand-orange mb-3">
            Get In Touch
          </span>
          <h2
            data-testid="contact-heading"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight uppercase text-brand-navy"
          >
            Contact Us
          </h2>
          <p className="font-body text-sm sm:text-base text-brand-slate mt-3 max-w-xl mx-auto">
            Ready to transform your home? Get in touch for a free estimate.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Contact Information */}
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {contactMethods.map((method) => {
                const Icon = method.icon;
                return (
                  <Card
                    key={method.title}
                    data-testid={`contact-method-${method.title.toLowerCase()}`}
                    className="p-6 bg-brand-ice border border-border rounded-sm hover:border-brand-orange/30 transition-colors duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-brand-navy rounded-sm flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-brand-orange" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-bold uppercase text-brand-navy">
                          {method.title}
                        </h3>
                        <p className="text-xs text-brand-slate mb-2">
                          {method.description}
                        </p>
                        {method.action ? (
                          <a
                            href={method.action}
                            className="text-sm text-brand-navy hover:text-brand-orange transition-colors whitespace-pre-line"
                          >
                            {method.value}
                          </a>
                        ) : (
                          <span className="text-sm text-brand-navy whitespace-pre-line">
                            {method.value}
                          </span>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Emergency Contact */}
            <Card className="p-6 bg-brand-navy text-white rounded-sm">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-brand-orange rounded-sm flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold uppercase">
                    Call Now
                  </h3>
                  <p className="text-sm text-white/70">
                    Free estimates available
                  </p>
                  <a
                    href="tel:+14079494656"
                    className="text-brand-orange font-medium hover:underline"
                  >
                    (407) 949-4656
                  </a>
                </div>
              </div>
            </Card>

            {/* Map Placeholder */}
            <div className="h-64 bg-brand-ice rounded-sm border border-border overflow-hidden">
              <iframe
                title="Roll Orlando Service Area"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d224444.95033584045!2d-81.57960064453125!3d28.481168699999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e773d8fecdbc77%3A0xac3b2063ca5bf9e!2sOrlando%2C%20FL!5e0!3m2!1sen!2sus!4v1635180000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Contact Form */}
          {showForm && (
            <div>
              {isSubmitted ? (
                <Card
                  data-testid="contact-success"
                  className="p-8 bg-brand-ice border border-border rounded-sm text-center"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold uppercase text-brand-navy mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-brand-slate mb-6">
                    Thank you for reaching out. We'll get back to you within 24
                    hours.
                  </p>
                  <Button
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({
                        name: "",
                        email: "",
                        phone: "",
                        subject: "",
                        message: "",
                      });
                    }}
                    variant="outline"
                    className="border-brand-navy text-brand-navy font-heading font-bold uppercase hover:bg-brand-navy hover:text-white"
                  >
                    Send Another Message
                  </Button>
                </Card>
              ) : (
                <Card className="p-8 bg-white border border-border rounded-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <MessageSquare className="w-6 h-6 text-brand-orange" />
                    <h3 className="font-heading text-xl font-bold uppercase text-brand-navy">
                      Send Us a Message
                    </h3>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name" className="text-brand-navy">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          data-testid="contact-name"
                          placeholder="John Smith"
                          value={formData.name}
                          onChange={(e) =>
                            handleInputChange("name", e.target.value)
                          }
                          className="mt-2 border-border focus:border-brand-orange"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-brand-navy">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="contact-email"
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={(e) =>
                            handleInputChange("email", e.target.value)
                          }
                          className="mt-2 border-border focus:border-brand-orange"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone" className="text-brand-navy">
                          Phone Number
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          data-testid="contact-phone-input"
                          placeholder="+00000-00000"
                          value={formData.phone}
                          onChange={(e) =>
                            handleInputChange("phone", e.target.value)
                          }
                          className="mt-2 border-border focus:border-brand-orange"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject" className="text-brand-navy">
                          Subject *
                        </Label>
                        <Input
                          id="subject"
                          data-testid="contact-subject"
                          placeholder="Project inquiry"
                          value={formData.subject}
                          onChange={(e) =>
                            handleInputChange("subject", e.target.value)
                          }
                          className="mt-2 border-border focus:border-brand-orange"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-brand-navy">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        data-testid="contact-message"
                        placeholder="Tell us about your painting project..."
                        value={formData.message}
                        onChange={(e) =>
                          handleInputChange("message", e.target.value)
                        }
                        className="mt-2 min-h-[150px] border-border focus:border-brand-orange"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                      className="w-full bg-brand-orange text-white font-heading font-bold uppercase hover:bg-brand-orange/90 h-12"
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          Send Message
                          <Send className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
